import { type IDataObjectIdentity, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID_LIST, RECEIVE_DATA_OBJECTS, RETRIEVE_BY_QUERY, UPDATE_DATA_OBJECT } from '@inpart-io/devtools-vue';
import { DataType, DocumentOriginValues, type IPrivateDocument, type IPrivateEmail, privateEmailsManager } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { ADD_SEARCH_RESULT, REMOVE_SEARCH_RESULT, SAVE_SEARCH_RESULT } from '@/store/base/search/actions';
import { PRIVATE_DOCUMENT_MODULE } from '@/store/private-document-module';

import { PRIVATE_EMAIL_MODULE } from './index';
export const IMPORT_EMAILS = 'importEmails';
export const ADD_EMAILS_TO_COMMENT = 'addEmailsToComment';
export const REMOVE_COMMENT_FROM_EMAILS = 'removeEmailsToComment';
export const RETRIEVE_EMAILS_FOR_COMMENT_LIST = 'retrieveEmailsForCommentList';

export const actions: ActionTree<IPrivateEmail, unknown> = {
    async [IMPORT_EMAILS](context, { executionId, data, params }: { executionId: string; data: unknown; params: unknown }): Promise<unknown> {
        const response = await privateEmailsManager.import(executionId, data, params);
        let importedEmails = [];
        let importedAttachments = [];
        if (response?.emails?.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: response.emails });
        }
        if (response?.attachments?.length > 0) {
            context.commit(`${PRIVATE_DOCUMENT_MODULE}/${RECEIVE_DATA_OBJECTS}`, { dataObjectList: response.attachments }, { root: true });
        }

        importedEmails = (response.emails ?? []).map((dataObject: IPrivateEmail) => {
            return dataObject.dobjId;
        });

        importedAttachments = (response.attachments ?? []).map((dataObject: IPrivateDocument) => {
            return dataObject.dobjId;
        });
        return { importedEmails, importedAttachments };
    },

    async [ADD_EMAILS_TO_COMMENT](
        context,
        { emailIdList, commentId, properties }: { emailIdList: number[]; commentId: number; properties?: string }
    ): Promise<void> {
        const newEmails = [] as IPrivateEmail[];
        for (const emailId of emailIdList) {
            newEmails.push({
                dobjId: emailId,
                links: {
                    '@value': [{ type: DataType.LeadComment, dobjId: commentId, queryScore: 0 }],
                    '@operation': 'ADD'
                },
                origin: DocumentOriginValues.COMMENT,
                originLink: { type: DataType.LeadComment, dobjId: commentId }
            });
        }
        const result = await context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: newEmails, properties });
        return context.dispatch(ADD_SEARCH_RESULT, { key: commentId.toString(), idList: result });
    },

    async [REMOVE_COMMENT_FROM_EMAILS](
        context,
        { emailIdList, commentId, properties }: { emailIdList: number[]; commentId: number; properties?: string }
    ): Promise<void> {
        const newEmails = [] as IPrivateEmail[];
        for (const emailId of emailIdList) {
            newEmails.push({
                dobjId: emailId,
                links: {
                    '@value': [{ type: DataType.LeadComment, dobjId: commentId, queryScore: 0 }],
                    '@operation': 'REMOVE'
                }
            });
        }
        const result = await context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: newEmails, properties });
        return context.dispatch(REMOVE_SEARCH_RESULT, { key: commentId.toString(), idList: result });
    },

    async [RETRIEVE_EMAILS_FOR_COMMENT_LIST](
        context,
        { commentIdList, mainLinkId, mainLinkType, properties }: { commentIdList: number[]; mainLinkId: number; mainLinkType: DataType; properties?: string }
    ): Promise<number> {
        const _comparators = [];
        _comparators.push(queryBuilder.cmp.equalTo('mainLink', mainLinkId));
        _comparators.push(queryBuilder.cmp.typeIs('mainLink', mainLinkType));
        _comparators.push(queryBuilder.cmp.isIn('links', commentIdList));
        const query = queryBuilder.build(queryBuilder.op.and(..._comparators));
        const emailIdList = await context.dispatch(RETRIEVE_BY_QUERY, {
            query,
            properties
        });
        const emailList = context.rootGetters[`${PRIVATE_EMAIL_MODULE}/${GET_BY_ID_LIST}`](emailIdList);
        const tempState: Record<string, number[]> = {};
        for (const email of emailList) {
            const linkedCommentIdList: string[] = email.links?.map((link: IDataObjectIdentity) => {
                return link.dobjId.toString();
            });

            for (const linkedCommentId of linkedCommentIdList) {
                if (tempState?.[linkedCommentId] === undefined || tempState?.[linkedCommentId] === null) {
                    tempState[linkedCommentId] = [];
                }
                tempState[linkedCommentId].push(email.dobjId);
            }
        }
        for (const commentId of Object.keys(tempState)) {
            void context.dispatch(SAVE_SEARCH_RESULT, { key: commentId.toString(), idList: tempState[commentId] });
        }
        return emailIdList;
    }
};
